import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Plus, Edit2, Trash2, Save, X, Clock, DollarSign,
  Package, Percent, AlertCircle, CheckCircle, Shield, RefreshCw,
  Loader2, ArrowLeftRight, ChevronDown, ChevronUp, Eye, ToggleLeft,
  ToggleRight, FileText, Tag, Wallet, CreditCard, Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { ReasonsEditor, CustomerPortalPreview } from '@/components/returns/ReasonsEditor';
import { ReturnPortalBrandingTab } from '@/components/returns/ReturnPortalBrandingTab';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Return type labels
const returnTypeLabels = {
  return_refund: 'Return & Refund',
  refund_only: 'Refund Only',
  replace_same: 'Replace (Same Product)',
  replace_different: 'Replace (Different Product)'
};

const ReturnRulesPage = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phase 3 — tab switcher: "rules" (existing) | "branding" (new)
  const [activeTab, setActiveTab] = useState('rules');
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_default: false,
    return_window_days: 7,
    allowed_return_types: ['return_refund'],
    requires_photos: false,
    requires_reason: true,
    auto_approve: false,
    auto_approve_conditions: [],
    restocking_fee_enabled: false,
    restocking_fee_type: 'percentage',
    restocking_fee_value: 0,
    restocking_fee_tiers: [],
    exclude_categories: [],
    exclude_skus: [],
    exclude_tags: [],
    exclusion_message: '',
    return_reasons: [],
    // Phase 2 — refund methods + Shopify store credit automation
    refund_method_options: ['original'],
    default_refund_method: 'original',
    auto_process_store_credit: false,
    store_credit_bonus_percent: 0
  });

  // Duplicate-name detection for reasons — populated by
  // <ReasonsEditor/> on every keystroke; the Save button is disabled
  // when this Set is non-empty (belt-and-braces with the server-side
  // 400 we raise for the same condition).
  const dupeReasonIdsRef = useRef(new Set());
  
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/returns/rules/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load return rules');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);
  
  const handleCreate = () => {
    setSelectedRule(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
      is_default: false,
      return_window_days: 7,
      allowed_return_types: ['return_refund'],
      requires_photos: false,
      requires_reason: true,
      auto_approve: false,
      auto_approve_conditions: [],
      restocking_fee_enabled: false,
      restocking_fee_type: 'percentage',
      restocking_fee_value: 0,
      restocking_fee_tiers: [],
      exclude_categories: [],
      exclude_skus: [],
      exclude_tags: [],
      exclusion_message: '',
      return_reasons: [],
      refund_method_options: ['original'],
      default_refund_method: 'original',
      auto_process_store_credit: false,
      store_credit_bonus_percent: 0
    });
    setEditDialogOpen(true);
  };
  
  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setFormData({
      name: rule.name || '',
      description: rule.description || '',
      is_active: rule.is_active !== false,
      is_default: rule.is_default || false,
      return_window_days: rule.return_window_days || 7,
      allowed_return_types: rule.allowed_return_types || ['return_refund'],
      requires_photos: rule.requires_photos || false,
      requires_reason: rule.requires_reason !== false,
      auto_approve: rule.auto_approve || false,
      auto_approve_conditions: rule.auto_approve_conditions || [],
      restocking_fee_enabled: rule.restocking_fee_enabled || false,
      restocking_fee_type: rule.restocking_fee_type || 'percentage',
      restocking_fee_value: rule.restocking_fee_value || 0,
      restocking_fee_tiers: rule.restocking_fee_tiers || [],
      exclude_categories: rule.exclude_categories || [],
      exclude_skus: rule.exclude_skus || [],
      exclude_tags: rule.exclude_tags || [],
      exclusion_message: rule.exclusion_message || '',
      // Server always returns the normalised dict shape; fall back
      // to [] for brand-new rules or legacy returns_reasons arrays
      // of plain strings (we pass-through — the BE upgrader handles it).
      return_reasons: Array.isArray(rule.return_reasons) ? rule.return_reasons : [],
      // Phase 2 — refund method config (default to "original" only if
      // absent on the legacy rule doc).
      refund_method_options: Array.isArray(rule.refund_method_options) && rule.refund_method_options.length
        ? rule.refund_method_options
        : ['original'],
      default_refund_method: rule.default_refund_method || 'original',
      auto_process_store_credit: !!rule.auto_process_store_credit,
      store_credit_bonus_percent: Number(rule.store_credit_bonus_percent || 0)
    });
    setEditDialogOpen(true);
  };
  
  const handleDelete = (rule) => {
    setSelectedRule(rule);
    setDeleteDialogOpen(true);
  };
  
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    if (dupeReasonIdsRef.current && dupeReasonIdsRef.current.size > 0) {
      toast.error('Two or more reasons share the same name — rename them before saving.');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        allowed_return_types: formData.allowed_return_types
      };
      
      if (selectedRule) {
        // Update existing rule
        await axios.put(`${API_BASE_URL}/v1/returns/rules/${selectedRule.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rule updated successfully');
      } else {
        // Create new rule
        await axios.post(`${API_BASE_URL}/v1/returns/rules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rule created successfully');
      }
      
      setEditDialogOpen(false);
      fetchRules();
    } catch (error) {
      // Pydantic 2 returns `detail` as an array of
      // `{loc, msg, type, input, url}` dicts. Passing an array of
      // objects directly into the toast causes React to try rendering
      // objects and crashes the whole page to a blank screen. Coerce
      // to a single human-readable line so the merchant sees what
      // went wrong instead of losing the dialog.
      const raw = error.response?.data?.detail;
      let msg = 'Failed to save rule';
      if (typeof raw === 'string' && raw.trim()) {
        msg = raw.split('\n')[0].slice(0, 240);
      } else if (Array.isArray(raw) && raw.length) {
        const f = raw[0] || {};
        const loc = (f.loc || []).filter((p) => p !== 'body').join('.');
        msg = `${loc || 'request'}: ${f.msg || 'invalid'}`;
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!selectedRule) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/v1/returns/rules/${selectedRule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted successfully');
      setDeleteDialogOpen(false);
      fetchRules();
    } catch (error) {
      const raw = error.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : 'Failed to delete rule');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleReturnType = (type) => {
    setFormData(prev => {
      const types = [...prev.allowed_return_types];
      const index = types.indexOf(type);
      if (index > -1) {
        types.splice(index, 1);
      } else {
        types.push(type);
      }
      return { ...prev, allowed_return_types: types };
    });
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-400" />
                Return Rules
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">
                Configure return policies and customer portal branding
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'rules' && (
                <>
                  <Button
                    onClick={() => fetchRules()}
                    variant="outline"
                    size="sm"
                    className="border-white/10 bg-white/5 hover:bg-white/10"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleCreate}
                    className="bg-purple-600 hover:bg-purple-700"
                    size="sm"
                    data-testid="create-rule-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Rule
                  </Button>
                </>
              )}
            </div>
          </div>
          {/* Phase 3 — Tab switcher */}
          <div className="flex items-center gap-1 mt-3 border-b border-white/5 -mb-4">
            {[
              { key: 'rules', label: 'Rules', icon: Shield },
              { key: 'branding', label: 'Portal Branding', icon: Palette }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  activeTab === key
                    ? 'border-purple-500 text-white'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                data-testid={`tab-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {activeTab === 'branding' ? (
        <div className="p-4">
          <ReturnPortalBrandingTab />
        </div>
      ) : (
      <>
      {/* Rules List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] rounded-xl border border-white/5">
            <Settings className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No return rules configured</p>
            <p className="text-sm text-zinc-600 mt-1">Create your first return rule to get started</p>
            <Button
              onClick={handleCreate}
              className="mt-4 bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Rule
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {rules.map((rule, index) => (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all"
                data-testid={`rule-card-${rule.id}`}
              >
                {/* Rule Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${rule.is_active ? 'bg-green-500/10' : 'bg-zinc-500/10'}`}>
                      <Shield className={`w-5 h-5 ${rule.is_active ? 'text-green-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-white">{rule.name}</h3>
                        {rule.is_default && (
                          <Badge className="bg-purple-500/20 text-purple-300 text-xs">Default</Badge>
                        )}
                        {!rule.is_active && (
                          <Badge className="bg-zinc-500/20 text-zinc-400 text-xs">Inactive</Badge>
                        )}
                      </div>
                      {rule.description && (
                        <p className="text-sm text-zinc-500 mt-0.5">{rule.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleEdit(rule)}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-white"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {!rule.is_default && (
                      <Button
                        onClick={() => handleDelete(rule)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Rule Details — summary derived from REASONS now
                    that the rule no longer carries window / restocking
                    / auto-approve / photo fields directly. We compute
                    a min..max window range and aggregate flags so a
                    merchant can scan rule cards at a glance without
                    opening every reason. */}
                {(() => {
                  const reasons = Array.isArray(rule.return_reasons) ? rule.return_reasons : [];
                  const windows = reasons.map(r => Number(r.return_window_days) || 0).filter(n => n > 0);
                  const fallbackWindow = Number(rule.return_window_days) || 0;
                  let windowLabel;
                  if (windows.length) {
                    const min = Math.min(...windows);
                    const max = Math.max(...windows);
                    windowLabel = min === max ? `${min} days` : `${min}–${max} days`;
                  } else if (fallbackWindow > 0) {
                    windowLabel = `${fallbackWindow} days`;
                  } else {
                    windowLabel = '—';
                  }

                  const restockReasons = reasons.filter(r => r.restocking_fee_enabled);
                  const restockLabel = restockReasons.length
                    ? `${restockReasons.length}/${reasons.length} reasons`
                    : (rule.restocking_fee_enabled
                        ? (rule.restocking_fee_type === 'percentage'
                            ? `${rule.restocking_fee_value}%`
                            : `₹${rule.restocking_fee_value}`)
                        : 'None');

                  const autoReasons = reasons.filter(r => r.auto_approve).length;
                  const autoLabel = autoReasons
                    ? `${autoReasons}/${reasons.length} auto`
                    : (rule.auto_approve_enabled || rule.auto_approve ? 'Enabled' : 'Disabled');

                  const photoReasons = reasons.filter(r => r.photo_required).length;

                  // Union of allowed_return_types across reasons (with
                  // legacy rule fallback) so the chip strip below shows
                  // every type the customer might be offered.
                  const typesSet = new Set();
                  reasons.forEach(r => (r.allowed_return_types || []).forEach(t => typesSet.add(t)));
                  if (!typesSet.size && Array.isArray(rule.allowed_return_types)) {
                    rule.allowed_return_types.forEach(t => typesSet.add(t));
                  }
                  const allowedTypes = [...typesSet];

                  return (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="bg-white/[0.02] rounded-lg p-3" data-testid="rule-card-window">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span className="text-xs">Return Window</span>
                          </div>
                          <p className="text-white font-medium">{windowLabel}</p>
                        </div>
                        <div className="bg-white/[0.02] rounded-lg p-3" data-testid="rule-card-restock">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            <span className="text-xs">Restocking Fee</span>
                          </div>
                          <p className="text-white font-medium">{restockLabel}</p>
                        </div>
                        <div className="bg-white/[0.02] rounded-lg p-3" data-testid="rule-card-auto">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span className="text-xs">Auto-Approve</span>
                          </div>
                          <p className={`font-medium ${autoReasons ? 'text-green-400' : 'text-zinc-400'}`}>{autoLabel}</p>
                        </div>
                        <div className="bg-white/[0.02] rounded-lg p-3" data-testid="rule-card-reqs">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <FileText className="w-3.5 h-3.5" />
                            <span className="text-xs">Reasons</span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap items-center">
                            <Badge className="bg-purple-500/10 text-purple-300 text-xs">{reasons.length}</Badge>
                            {photoReasons > 0 && (
                              <Badge className="bg-blue-500/10 text-blue-400 text-xs">📷 {photoReasons}</Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Allowed Return Types — union across reasons */}
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                          <ArrowLeftRight className="w-3.5 h-3.5" />
                          <span>Allowed Return Types</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {allowedTypes.length === 0 && <span className="text-zinc-600 text-xs">No reasons configured yet</span>}
                          {allowedTypes.map(type => (
                            <Badge key={type} className="bg-purple-500/10 text-purple-300 text-xs">
                              {returnTypeLabels[type] || type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })()}
                
                {/* Exclusions */}
                {(rule.exclude_categories?.length > 0 || rule.exclude_skus?.length > 0 || rule.exclude_tags?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>Exclusions</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {rule.exclude_categories?.map((cat, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-red-500/30 text-red-400">
                          Category: {cat}
                        </Badge>
                      ))}
                      {rule.exclude_skus?.map((sku, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-red-500/30 text-red-400">
                          SKU: {sku}
                        </Badge>
                      ))}
                      {rule.exclude_tags?.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-red-500/30 text-red-400">
                          Tag: {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
      </>
      )}
      
      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              {selectedRule ? 'Edit Return Rule' : 'Create Return Rule'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 py-4">
            <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Rule Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="e.g., Standard Return Policy"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="Brief description of this rule..."
                    rows={2}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Set as Default</Label>
                  <Switch
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                  />
                </div>
              </div>
            </div>
            
            {/* Return Reasons — each reason owns its full policy now
                (window / allowed types / photo / auto-approve /
                restocking incl tiers / refund methods). The previous
                rule-level sections (Window & Types, Requirements,
                Auto-Approve, Restocking Fee, Refund Methods) have
                been folded into the per-reason editor below. */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Return Reasons
                </h3>
                <span className="text-[11px] text-zinc-500">
                  Each reason owns its own policy — window, types, photo, restocking (with tiers), auto-approve, refund methods.
                </span>
              </div>
              <ReasonsEditor
                reasons={formData.return_reasons}
                onChange={(next) => setFormData(prev => ({ ...prev, return_reasons: next }))}
                duplicatesRef={dupeReasonIdsRef}
              />
            </div>

            {/* Shopify Store Credit (advanced) — applies when ANY
                reason offers store credit as a refund method. */}
            {formData.return_reasons?.some(r => (r.refund_method_options || []).includes('store_credit')) && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  Shopify Store Credit (advanced)
                </h3>
                <div className="p-3 bg-white/[0.02] rounded-lg space-y-3 border border-purple-500/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="cursor-pointer">Auto-issue Shopify Store Credit on refund</Label>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        When a return is marked refunded with method = Store Credit, automatically credit the customer's Shopify Store Credit account.
                      </p>
                    </div>
                    <Switch
                      data-testid="auto-store-credit-toggle"
                      checked={!!formData.auto_process_store_credit}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_process_store_credit: v }))}
                    />
                  </div>
                  <div>
                    <Label>Store Credit Bonus (%) <span className="text-zinc-500 text-xs">— optional incentive</span></Label>
                    <div className="relative mt-1 w-40">
                      <Input
                        type="number"
                        data-testid="store-credit-bonus-input"
                        value={formData.store_credit_bonus_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, store_credit_bonus_percent: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) }))}
                        className="bg-white/5 border-white/10 pr-8"
                        min={0}
                        max={100}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                    </div>
                    {formData.store_credit_bonus_percent > 0 && (
                      <p className="text-[11px] text-emerald-400 mt-1">
                        Customers who pick store credit will receive {formData.store_credit_bonus_percent}% extra value.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Exclusions */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Exclusions (comma-separated)
              </h3>
              <div className="space-y-3">
                <div>
                  <Label>Exclude Categories</Label>
                  <Input
                    value={formData.exclude_categories?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      exclude_categories: e.target.value ? e.target.value.split(',').map(s => s.trim()) : [] 
                    }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="e.g., Electronics, Perishables"
                  />
                </div>
                <div>
                  <Label>Exclude SKUs</Label>
                  <Input
                    value={formData.exclude_skus?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      exclude_skus: e.target.value ? e.target.value.split(',').map(s => s.trim()) : [] 
                    }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="e.g., SKU-001, SKU-002"
                  />
                </div>
                <div>
                  <Label>Exclude Tags</Label>
                  <Input
                    value={formData.exclude_tags?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      exclude_tags: e.target.value ? e.target.value.split(',').map(s => s.trim()) : [] 
                    }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="e.g., final-sale, clearance"
                  />
                </div>
                <div>
                  <Label>Customer Message for Excluded Items <span className="text-zinc-500 text-xs">— shown on the portal</span></Label>
                  <Textarea
                    value={formData.exclusion_message || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, exclusion_message: e.target.value.slice(0, 300) }))}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder='e.g., "Final sale items can&apos;t be returned — contact support for help"'
                    rows={2}
                    maxLength={300}
                    data-testid="exclusion-message-input"
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">Customers see this under any item that can't be returned. Max 300 characters.</p>
                </div>
              </div>
            </div>
            </div>

            {/* Right column — live customer-portal preview */}
            <div className="hidden lg:block">
              <CustomerPortalPreview
                rule={formData}
                reasons={formData.return_reasons}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedRule ? 'Save Changes' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Return Rule
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300">
              Are you sure you want to delete <span className="font-medium text-white">{selectedRule?.name}</span>?
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              This action cannot be undone. Returns using this rule will fall back to the default rule.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} disabled={saving} variant="destructive">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturnRulesPage;
